import { BaseObject } from '../base-object';
import { Case } from '../case';
import { SimulationVariable } from '../simulation-variable';
import { AssayManagerFluidAnalysisResourceData } from './assay-manager-resource-data';
import { FluidAnalysis } from './fluid-analysis';
import { ReadyStatus } from '../../_config/ready-status.enum';

export class GasFluidAnalysis extends FluidAnalysis {
  category = 'gas-fluid-analysis';
  assayName: string;
  ownerBaseObject: BaseObject;
  assayType: string;

  composition: Array<SimulationVariable>;

  upperBound: number;
  lowerBound: number;
  readyStatus: ReadyStatus;
  quantity: string;

  constructor(gasFluidAnalysis: any, ownerCase: Case, ownerBaseObject?: BaseObject) {
    super(gasFluidAnalysis, ownerCase);
    if (gasFluidAnalysis.composition) {
      if (gasFluidAnalysis.composition.length < 14) {
        gasFluidAnalysis.composition = [];
      }
    }

    this.ownerBaseObject = ownerBaseObject;
    this.assayName = gasFluidAnalysis.assayName || '';
    this.assayType = gasFluidAnalysis.assayType || '';
    this.composition = [];

    this.initSimVars();

    this.initComposition(gasFluidAnalysis.composition || []);

    this.setQuantities();

    this.upperBound = gasFluidAnalysis.upperBound;
    this.lowerBound = gasFluidAnalysis.lowerBound;
    this.readyStatus = gasFluidAnalysis.readyStatus || ReadyStatus.UNDEFINED;
  }

  override addSimVarsToPool() {
    super.addSimVarsToPool();
    for (const c of this.composition) {
      this.ownerCase.addToPools(c);
    }
  }

  override overwriteValues(another: BaseObject) {
    super.overwriteValues(another);

    const fg = another as GasFluidAnalysis;

    for (let i = 0; i < this.composition.length; i++) {
      this.composition[i].overwriteValues(fg.composition[i]);
    }
  }

  override convertToInternalUnit() {
    super.convertToInternalUnit();
  }

  protected override ignoreForOverWrite(propertyName: string): boolean {
    return super.ignoreForOverWrite(propertyName) || this[propertyName] instanceof Array;
  }

  override getName() {
    return this.ownerBaseObject.getName();
  }

  setSimulationVariableNames(): void {}

  override setSimulationVariablesOwner(): void {
    super.setSimulationVariablesOwner();
    for (let i = 0; i < this.composition.length; i++) {
      this.composition[i].setSimulationVariablesOwner();
    }
  }

  initSimVars(): void {}

  setOwnerBaseObject(ownerBaseObject: BaseObject): void {
    this.ownerBaseObject = ownerBaseObject;
  }

  initComposition(composition) {
    for (let idx = 0; idx < 14; idx++) {
      if (composition[idx] instanceof SimulationVariable || typeof composition[idx] === 'undefined') {
        this.composition.push(new SimulationVariable(composition[idx], this.ownerCase, this));
      }
    }

    this.setCompositionNames();
  }

  getCompositionElement(name: string): SimulationVariable {
    return this.composition.find(sv => sv.name === name);
  }

  setCompositionNames() {
    const names = [
      'CO',
      'CO2',
      'H2',
      'N2',
      'H2S',
      'SO2',
      'Methane',
      'Ethane',
      'Propane',
      'IC4',
      'NC4',
      'IC5',
      'NC5',
      'C6+',
    ];
    if (this.composition.length === 14) {
      for (let idx = 0; idx < 14; idx++) {
        this.composition[idx].setName(names[idx]);
      }
    }
  }

  setQuantities() {
    for (const sv of this.composition) {
      sv.setQuantity('MoleFraction');
    }

    this.quantity = 'MoleFraction';
  }

  override detectChanges(another: GasFluidAnalysis) {
    let changes = super.detectChanges(another);

    for (let idx = 0; idx < another.composition.length; idx++) {
      changes = changes || this.composition[idx].detectValueChange(another.composition[idx]);
    }
    return changes;
  }

  override getSimVars() {
    const simVars = super.getSimVars();
    for (const ce of this.composition) {
      simVars.push(ce);
    }

    return simVars;
  }

  protected override isPropertyBlackListed(property: any): boolean {
    return property instanceof Case || typeof property === 'function' || property === this.ownerBaseObject;
  }

  override getChildrenObjectsForRemoval(): BaseObject[] {
    return super.getChildrenObjectsForRemoval();
  }

  loadResourceData(resourceData: AssayManagerFluidAnalysisResourceData) {
    this.upperBound = resourceData.composition.upperBound;
    this.lowerBound = resourceData.composition.lowerBound;

    for (const sv of this.composition) {
      sv.upperBound = resourceData.composition.upperBound;
      sv.lowerBound = resourceData.composition.lowerBound;
    }
  }

  public toJSON(): any {
    const objectToSerialize: any = {};
    let val: any;
    for (val in this) {
      if (this[val] instanceof SimulationVariable) {
        objectToSerialize[val] = (<SimulationVariable>this[val]).id;
      } else if (this[val] === this.composition) {
        objectToSerialize.composition = [];
        for (const c of this.composition) {
          objectToSerialize.composition.push(c.id);
        }
      } else if (this.isPropertyBlackListed(this[val])) {
        // bruh
      } else {
        objectToSerialize[val] = this[val];
      }
    }

    return objectToSerialize;
  }

  override dePersist(gfa: any) {
    if (gfa.composition.length === 14) {
      for (let idx = 0; idx < 14; idx++) {
        this.composition.push(this.ownerCase.getSimulationVariable(gfa.composition[idx]));
      }
      this.setCompositionNames();
    }
  }
}
